import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import useOffers from 'hooks/Offers/useOffers'
import useOffer from 'hooks/Offers/useOffer'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import FinePrintModal from '../../Chat/MessageMeta/FinePrintModal'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { useAppDispatch } from 'hooks/reduxHooks'

interface Props {
  order: App.Order
  isPastOrder: boolean
  isCurrentOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}

export default function BundleAndSavePurchaseSummary(props: Props) {
  const intl = useIntl()
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap, isCurrentOrder, canEdit } = props

  const items = order.items

  const dispatch = useAppDispatch()

  const [childOffers, isFetchingOffers] = useOffers<App.Offer>(
    items?.map((item) => item.offer?.id),
  )

  const locationLabel = useMemo(
    () =>
      childOffers
        ?.map((offer) =>
          offer.locationHeading && offer.locationSubheading
            ? intl.formatMessage(
                {
                  id: 'OJWx1ok+e8',
                  defaultMessage: '{locationHeading}, {locationSubheading}',
                  description: 'Support assistant bundle and save location heading and subheading separated by comma',
                },
                { locationHeading: offer.locationHeading, locationSubheading: offer.locationSubheading },
              )
            : (offer.locationHeading || offer.locationSubheading || ''),
        )
        .join(' & '),
    [childOffers, intl],
  )

  const propertyLabel = useMemo(
    () => childOffers?.map((offer) => offer.property?.name).join(' & '),
    [childOffers],
  )

  // TEMP until we do select item on bundle and save
  const item = items?.[0]
  const [offer, isFetchingItemOffer] = useOffer<App.BundleOffer>(item?.offerId)

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: 'selectOrder', payload: { id_orders: order.id, isCurrentOrder, canEditOrder: canEdit } }))
  }, [dispatch, order.id, isCurrentOrder, canEdit])

  if (!offer || isFetchingOffers || isFetchingItemOffer) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions']))

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader order={order}>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineBedIcon size="XS" />}>
              {intl.formatMessage({
                id: 'vyid8o3CcW',
                defaultMessage: 'Hotel',
                description: 'Category label for bundle and save hotel purchase summary',
              })}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
              id: 'lLEh4fRMsg',
              defaultMessage: 'Upcoming',
              description: 'Status label for upcoming bundle and save booking',
            })}
            </StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
              id: '1QrUVHcT/u',
              defaultMessage: 'Past',
              description: 'Status label for past bundle and save booking',
            })}
            </StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >{locationLabel}
              </CaptionTextBlock>
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {propertyLabel}
              </LesaPurchaseSummaryTitle>
              {order.items?.map((item) => {
                const nightsLabel = intl.formatMessage({
                  id: '9CqP/FQccu',
                  defaultMessage: '{count, plural, one {# night} other {# nights}}',
                  description: 'Number of nights for bundle and save stay duration',
                }, { count: item.package?.duration ?? 0 })
                const durationLabel = item.package?.duration ? nightsLabel : undefined
                return (
                  <LesaPurchaseSummaryItineraryDates
                    key={item.id}
                    status={item.status}
                    startDate={item.reservation?.startDate}
                    endDate={item.reservation?.endDate}
                    duration={durationLabel}
                    bookingNumber={item.bookingNumber}
                    showReservationDates={!!item.reservation?.startDate}
                  />
                )
              })}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} kind="secondary" fit="mobile-full-width" size="small">
                {intl.formatMessage({
                  id: 'fxO9+N0zjG',
                  defaultMessage: 'Select',
                  description: 'Button text to select a bundle and save booking',
                })}
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!componentsMap?.get('fine-print-modal') && (
        <FinePrintModal offer={offer} />
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}
